import { ChangeDetectionStrategy, Component, DOCUMENT, DestroyRef, computed, effect, inject, untracked } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { AppSettingsPanelService } from '@core/services/app-settings-panel.service';
import { HdChartSettingsPanelComponent } from '@/app/human-design/chart/bodygraph/chart-tabs/hd-chart-settings-panel.component';
import { HdChartSettingsStore } from '@/app/human-design/chart/bodygraph/chart-tabs/hd-chart-settings.store';
import { applyHdHoverHighlightTheme } from '@/app/human-design/chart/bodygraph/hd-hover-highlight-theme';
import { HdIChingAccessService } from '@/app/human-design/iching/hd-iching-access.service';
import { HdLineTextReviewAccessService } from '@/app/human-design/line-text-review/hd-line-text-review-access.service';
import { HdLineTextReviewPanelService } from '@/app/human-design/line-text-review/panel/hd-line-text-review-panel.service';

/**
 * App-shell van de HD-app: rendert de actieve HD-pagina in de router-outlet en
 * host het énige applicatie-instellingenpaneel, gebonden aan de centrale
 * {@link HdChartSettingsStore}. Zo is het paneel overal **binnen** de HD-app te
 * openen (elke HD-pagina zit onder deze shell) en enkel daar (de shell — en dus
 * de beschikbaarheid — bestaat alleen binnen de HD-app).
 *
 * Het menu-item ("Applicatie-instellingen") vraagt het openen aan via
 * {@link AppSettingsPanelService}; deze shell consumeert dat verzoek en meldt de
 * beschikbaarheid, zodat platform-core losgekoppeld blijft van human-design.
 */
@Component({
  selector: 'hd-app-shell',
  standalone: true,
  imports: [RouterOutlet, HdChartSettingsPanelComponent],
  templateUrl: './hd-app-shell.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdAppShellComponent {
  protected readonly store = inject(HdChartSettingsStore);
  /** Classic I Ching-sectie in het paneel bestaat alleen met de UPAM-feature. */
  protected readonly ichingAccess = inject(HdIChingAccessService);
  protected readonly lineTextReviewAccess = inject(HdLineTextReviewAccessService);
  protected readonly lineTextReviewPanel = inject(HdLineTextReviewPanelService);
  private readonly appSettingsPanel = inject(AppSettingsPanelService);

  /** Print enkel wanneer een chart-weergave een print-actie registreerde. */
  protected readonly showPrint = computed(() => this.store.printHandler() != null);

  constructor() {
    this.appSettingsPanel.setAvailable(true);
    inject(DestroyRef).onDestroy(() => this.appSettingsPanel.setAvailable(false));

    // Hoverkleur als CSS-variabelen op <html>: zo volgen ook CDK-overlays
    // (buiten deze host) dezelfde kleur. Bij het verlaten van de HD-app weer
    // weg, zodat elke usage terugvalt op zijn eigen blauw.
    const rootStyle = inject(DOCUMENT).documentElement.style;
    effect(() => applyHdHoverHighlightTheme(rootStyle, this.store.hoverHighlightColor()));
    inject(DestroyRef).onDestroy(() => applyHdHoverHighlightTheme(rootStyle, null));

    // Menu-verzoek → paneel openen (op elke HD-pagina; geen navigatie nodig).
    effect(() => {
      if (this.appSettingsPanel.pendingOpen()) {
        untracked(() => {
          this.store.panelOpen.set(true);
          this.appSettingsPanel.clearPending();
        });
      }
    });
  }

  protected onPrint(): void {
    this.store.printHandler()?.();
  }
}
